export class NodeMigrationViewElementImpl extends HTMLElement {
    #client;
    #state = { status: "ready" };
    #running = false;
    set client(value) {
        this.#client = value;
        if (this.isConnected)
            void this.refresh();
    }
    get client() { return this.#client; }
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() { return this.#state; }
    connectedCallback() {
        this.#render();
        if (this.#client !== undefined)
            void this.refresh();
    }
    async refresh() {
        if (this.#client === undefined || this.#running)
            return;
        this.#running = true;
        this.state = { status: "running", step: "compatibility-check" };
        try {
            this.state = await this.#client.command("client-migration.status.v1", {});
        }
        catch {
            this.state = { message: "Migration status is temporarily unavailable.", status: "error" };
        }
        finally {
            this.#running = false;
        }
    }
    async #run(command) {
        if (this.#client === undefined || this.#running)
            return;
        this.#running = true;
        this.state = { status: "running", step: "compatibility-check" };
        try {
            this.state = await this.#client.command(command, {});
        }
        catch {
            this.state = { message: "Migration could not continue. Retry to resume the saved journal.", status: "error" };
        }
        finally {
            this.#running = false;
        }
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        // Same skin-token treatment (and same rationale) as pairing/pairing-view.ts — this
        // view previously shared its off-brand navy palette verbatim; see that file's
        // doc comment for the token-inheritance and light-dark() reasoning.
        style.textContent = `
      :host { display: block; min-height: 100%; background: var(--bg-0, #FFFFFF); color: var(--ink-hi, #0A0A0A); font: 16px/1.5 var(--ui-font, Inter, system-ui, sans-serif); }
      main { max-width: 42rem; margin: 0 auto; padding: clamp(2rem, 8vw, 7rem) 1.25rem; }
      section { display: grid; gap: 1rem; padding: clamp(1.25rem, 4vw, 2.25rem); border: 1px solid var(--hr, rgba(10, 10, 10, 0.07)); border-radius: var(--ui-radius-lg, 14px); background: var(--bg-raised, #FFFFFF); box-shadow: var(--ui-shadow-2, 0 1px 1px rgba(10, 10, 10, 0.06), 0 24px 48px -24px rgba(10, 10, 10, 0.4)); }
      h1, p { margin: 0; } h1 { font: 400 clamp(1.75rem, 5vw, 2.75rem)/1.15 var(--ui-serif, "Instrument Serif", serif); } p { color: var(--ink-mid, #545454); }
      button { min-height: 2.75rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px); padding: .65rem 1.1rem; font: 500 .9375rem/1.2 var(--ui-font, Inter, system-ui, sans-serif); color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out); }
      button:hover { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); color: #FFFFFF; }
      button:focus-visible { outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px; }
      [role=alert] { color: light-dark(var(--c-coral-deep, #B85551), var(--c-coral, #DF6662)); }
      code { font-family: var(--ui-mono, "JetBrains Mono", ui-monospace, monospace); color: var(--ink-hi, #0A0A0A); }
      @media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; } }
      @media (forced-colors: active) { section, button { border: 1px solid CanvasText; } }
    `;
        const main = document.createElement("main");
        const section = document.createElement("section");
        section.setAttribute("aria-live", "polite");
        const heading = document.createElement("h1");
        const copy = document.createElement("p");
        const actions = document.createElement("div");
        actions.setAttribute("data-migration-actions", "true");
        switch (this.#state.status) {
            case "blocked":
                heading.textContent = "Migration blocked";
                copy.textContent = this.#state.message ?? "Upgrade the home node before migrating this browser.";
                break;
            case "completed":
                heading.textContent = "Migration complete";
                copy.textContent = "This browser is paired and authoritative data has been resynced.";
                break;
            case "error":
                heading.textContent = "Migration unavailable";
                copy.textContent = this.#state.message ?? "Retry when the home node is available.";
                copy.setAttribute("role", "alert");
                break;
            case "not-needed":
                heading.textContent = "No migration needed";
                copy.textContent = "This is a fresh browser install.";
                break;
            case "paused":
                heading.textContent = "Migration paused";
                copy.textContent = this.#state.message ?? "The saved journal can resume from the last completed step.";
                this.#appendResumeButton(actions);
                break;
            case "running":
                heading.textContent = "Migrating this browser";
                copy.textContent = "Keep this tab open while the saved migration journal advances.";
                break;
            case "ready":
                heading.textContent = "Migration available";
                copy.textContent = "Pair this browser, import safe preferences, and resync authoritative data.";
                this.#appendStartButton(actions);
                break;
        }
        if (this.#state.step !== undefined) {
            const step = document.createElement("p");
            step.append("Current step: ", document.createElement("code"));
            step.querySelector("code").textContent = this.#state.step;
            section.append(step);
        }
        section.prepend(heading, copy);
        if (actions.childElementCount > 0)
            section.append(actions);
        main.append(section);
        root.append(style, main);
    }
    #appendStartButton(actions) {
        const button = this.#button("Start migration", "start");
        button.addEventListener("click", () => void this.#run("client-migration.run.v1"));
        actions.append(button);
    }
    #appendResumeButton(actions) {
        const button = this.#button("Resume migration", "resume");
        button.addEventListener("click", () => void this.#run("client-migration.resume.v1"));
        actions.append(button);
    }
    #button(label, action) {
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.migrationAction = action;
        button.textContent = label;
        return button;
    }
}
export function registerNodeMigrationView() {
    if (customElements.get("node-migration-view") === undefined) {
        customElements.define("node-migration-view", NodeMigrationViewElementImpl);
    }
}
//# sourceMappingURL=migration-view.js.map